export const PRESS_LOCATIONS = ['front', 'back', 'other'];

export const MESH_COUNTS = [110, 156, 195, 230];

export const FLASH_HEAD = 2;
export const ROLLER_HEAD = 5;
export const UB_HEAD = 1;

/** Heads used for color screens, highest first. Head 1 reserved for UB. */
export const COLOR_HEAD_ORDER = [8, 7, 6, 5, 4, 3, 2, 1];

export const isUnderbaseColor = (color) => {
  const normalized = String(color || '').trim().toUpperCase().replace(/\s+/g, '');
  return normalized === 'UB' || normalized === 'UNDERBASE';
};

export const defaultMeshForColor = (color) =>
  isUnderbaseColor(color) ? 156 : 195;

export const resolveMeshCount = (screen) => {
  const mesh = screen?.meshCount;
  if (mesh !== null && mesh !== undefined && mesh !== '') {
    return Number(mesh);
  }
  return defaultMeshForColor(screen?.color);
};

const normalizeColorKey = (color) =>
  String(color || '').trim().toLowerCase();

/** 1-based occurrence of this color at index (for duplicate ink slots). */
export const getColorOccurrenceIndex = (colors, index) => {
  const key = normalizeColorKey(colors[index]);
  if (!key) return 0;
  let count = 0;
  for (let i = 0; i <= index; i += 1) {
    if (normalizeColorKey(colors[i]) === key) count += 1;
  }
  return count;
};

export const colorAppearsMultipleTimes = (colors, index) => {
  const key = normalizeColorKey(colors[index]);
  if (!key) return false;
  let count = 0;
  for (const color of colors) {
    if (normalizeColorKey(color) === key) count += 1;
  }
  return count >= 2;
};

/** First slot of a duplicated color acts as underbase on head 1 (instead of white UB). */
export const isDuplicateUnderbaseEntry = (colors, index) => {
  if (!colorAppearsMultipleTimes(colors, index)) return false;
  if (isUnderbaseColor(colors[index])) return false;
  return getColorOccurrenceIndex(colors, index) === 1;
};

const findSavedScreenByColorOccurrence = (savedScreens, colors, targetIndex) => {
  const targetKey = normalizeColorKey(colors[targetIndex]);
  const targetOccurrence = getColorOccurrenceIndex(colors, targetIndex);
  if (!targetKey || !targetOccurrence) return null;

  let seen = 0;
  for (const screen of savedScreens) {
    if (normalizeColorKey(screen.color) === targetKey) {
      seen += 1;
      if (seen === targetOccurrence) return screen;
    }
  }
  return null;
};

export const emptyLocationSetup = () => ({
  flashEnabled: false,
  rollerEnabled: false,
  screens: [],
  notes: ''
});

export const getBlockedHeads = (flashEnabled, rollerEnabled) => {
  const blocked = new Set();
  if (flashEnabled) blocked.add(FLASH_HEAD);
  if (rollerEnabled) blocked.add(ROLLER_HEAD);
  return blocked;
};

/** Heads available for non-UB ink colors (fills from 8 backward). */
export const getColorHeadSlots = (flashEnabled, rollerEnabled) => {
  const blocked = getBlockedHeads(flashEnabled, rollerEnabled);
  return COLOR_HEAD_ORDER.filter((h) => h !== UB_HEAD && !blocked.has(h));
};

export const normalizeInkHex = (color) => {
  if (!color) return null;
  const s = String(color).trim();
  if (isUnderbaseColor(s)) return '#F5F5F5';
  const raw = s.startsWith('#') ? s.slice(1) : s;
  if (/^[0-9A-Fa-f]{6}$/.test(raw)) return `#${raw}`;
  if (/^[0-9A-Fa-f]{3}$/.test(raw)) {
    return `#${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}`;
  }
  return null;
};

const normalizeInkName = (name) =>
  String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');

export const findInkInCatalog = (color, inkCatalog = []) => {
  const key = normalizeInkName(color);
  if (!key || !Array.isArray(inkCatalog) || inkCatalog.length === 0) return null;

  const exact = inkCatalog.find((ink) => normalizeInkName(ink.name) === key);
  if (exact) return exact;

  const candidates = inkCatalog.filter((ink) => {
    const inkName = normalizeInkName(ink.name);
    if (!inkName || !ink.hexCode) return false;
    return inkName === key || inkName.includes(key) || key.includes(inkName);
  });

  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];

  return candidates.sort((a, b) => {
    const aName = normalizeInkName(a.name);
    const bName = normalizeInkName(b.name);
    if (aName === key) return -1;
    if (bName === key) return 1;
    return aName.length - bName.length;
  })[0];
};

export const resolveInkDisplayHex = (color, inkCatalog = []) => {
  const direct = normalizeInkHex(color);
  if (direct) return direct;
  const key = normalizeInkName(color);
  if (!key) return null;
  if (/\bwhite\b/.test(key)) return '#FFFFFF';
  if (/\bblack\b/.test(key)) return '#000000';
  const match = findInkInCatalog(color, inkCatalog);
  if (match?.hexCode) return normalizeInkHex(match.hexCode);
  return null;
};

const luminanceFromHex = (hex) => {
  const normalized = normalizeInkHex(hex);
  if (!normalized) return null;
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

export const getInkLuminance = (color, inkCatalog = []) => {
  const fromHex = luminanceFromHex(resolveInkDisplayHex(color, inkCatalog));
  if (fromHex != null) return fromHex;

  const key = normalizeInkName(color);
  if (!key) return 0.5;
  if (/\bwhite\b|snow|cream|ivory|natural\b/.test(key)) return 0.95;
  if (/\bblack\b|jet|licorice\b/.test(key)) return 0.08;
  if (/\blight\b|\bsky\b|baby|powder|pastel|lemon|yellow|gold\b/.test(key)) return 0.72;
  if (/\bnavy\b|blueberry|sapphire|midnight|burgundy|maroon|forest|salsa|cardinal|wine|chocolate|plum\b/.test(key)) {
    return 0.28;
  }
  return 0.5;
};

export const applyDefaultHeadLayout = (locationSetup, inkCatalog = []) => {
  const screens = [...(locationSetup.screens || [])];
  const explicitUB = screens.filter((s) => isUnderbaseColor(s.color));

  const duplicateColorKeys = new Set();
  const colorCounts = new Map();
  for (const screen of screens) {
    if (isUnderbaseColor(screen.color)) continue;
    const key = normalizeColorKey(screen.color);
    if (!key) continue;
    colorCounts.set(key, (colorCounts.get(key) || 0) + 1);
    if (colorCounts.get(key) >= 2) duplicateColorKeys.add(key);
  }

  const duplicateUnderbaseScreens = [];
  const colorScreens = [];
  const seenDuplicateBase = new Set();

  for (const screen of screens) {
    if (isUnderbaseColor(screen.color)) continue;
    const key = normalizeColorKey(screen.color);
    if (duplicateColorKeys.has(key) && !seenDuplicateBase.has(key)) {
      seenDuplicateBase.add(key);
      duplicateUnderbaseScreens.push(screen);
    } else {
      colorScreens.push(screen);
    }
  }

  const hasUB = explicitUB.length > 0 || duplicateUnderbaseScreens.length > 0;
  const flashEnabled = hasUB;
  const rollerEnabled = hasUB;
  const slots = getColorHeadSlots(flashEnabled, rollerEnabled);

  const assignedColors = colorScreens
    .sort(
      (a, b) =>
        getInkLuminance(a.color, inkCatalog) - getInkLuminance(b.color, inkCatalog)
    )
    .map((screen, i, sorted) => ({
      ...screen,
      meshCount: defaultMeshForColor(screen.color),
      head: sorted.length > 0 ? slots[sorted.length - 1 - i] ?? null : null
    }));

  const assignedDuplicateUnderbases = duplicateUnderbaseScreens.map((screen) => ({
    ...screen,
    meshCount: defaultMeshForColor(screen.color),
    head: UB_HEAD
  }));

  const assignedExplicitUB = explicitUB.map((screen) => ({
    ...screen,
    meshCount: defaultMeshForColor(screen.color),
    head: UB_HEAD
  }));

  const ordered = [
    ...assignedExplicitUB,
    ...assignedDuplicateUnderbases,
    ...assignedColors
  ].map((screen, index) => ({
    ...screen,
    printOrder: index
  }));

  return { ...locationSetup, flashEnabled, rollerEnabled, screens: ordered };
};

export const syncLocationSetupWithInks = (savedLocation, inkColors) => {
  const colors = Array.isArray(inkColors) ? inkColors.filter(Boolean) : [];
  if (colors.length === 0) return null;

  const flashEnabled = savedLocation?.flashEnabled === true;
  const rollerEnabled = savedLocation?.rollerEnabled === true;
  const savedScreens = savedLocation?.screens || [];

  const screens = colors.map((color, index) => {
    const byIndex = savedScreens[index];
    const existing =
      byIndex && normalizeColorKey(byIndex.color) === normalizeColorKey(color)
        ? byIndex
        : findSavedScreenByColorOccurrence(savedScreens, colors, index);

    const screen = {
      color,
      meshCount:
        existing?.meshCount != null && existing.meshCount !== ''
          ? Number(existing.meshCount)
          : defaultMeshForColor(color),
      head: existing?.head ?? null,
      printOrder: existing?.printOrder ?? index
    };
    return screen;
  });

  screens.sort((a, b) => a.printOrder - b.printOrder);
  screens.forEach((screen, index) => {
    screen.printOrder = index;
  });

  return {
    flashEnabled,
    rollerEnabled,
    screens,
    notes: savedLocation?.notes || ''
  };
};

export const syncPressSetupWithLine = (pressSetup, lineData) => {
  const next = {};
  let hasAny = false;

  for (const loc of PRESS_LOCATIONS) {
    const inkKey = `${loc}InkColors`;
    const inks = lineData?.[inkKey];
    const synced = syncLocationSetupWithInks(pressSetup?.[loc], inks);
    if (synced) {
      next[loc] = synced;
      hasAny = true;
    }
  }

  return hasAny ? next : null;
};

export const hasPressSetupData = (pressSetup) => {
  if (!pressSetup || typeof pressSetup !== 'object') return false;
  return PRESS_LOCATIONS.some((loc) => {
    const s = pressSetup[loc];
    if (!s) return false;
    if (s.notes?.trim()) return true;
    return Array.isArray(s.screens) && s.screens.length > 0;
  });
};

/** True when any location (front/back/other) has text in its press-setup notes box. */
export const hasPressSetupNotes = (pressSetup) => {
  if (!pressSetup || typeof pressSetup !== 'object') return false;
  return PRESS_LOCATIONS.some((loc) => String(pressSetup[loc]?.notes || '').trim());
};

/** Tooltip/preview text for press-setup notes across locations. */
export const getPressSetupNotesPreview = (pressSetup) => {
  if (!pressSetup || typeof pressSetup !== 'object') return '';
  const parts = [];
  for (const loc of PRESS_LOCATIONS) {
    const text = String(pressSetup[loc]?.notes || '').trim();
    if (text) parts.push(`${loc}: ${text}`);
  }
  return parts.join(' · ');
};

export const PRESS_SETUP_LAYOUT_STATUS = {
  UNSET: 'unset',
  DEFAULT: 'default',
  CUSTOM: 'custom'
};

export const PRESS_SETUP_STATUS_LABELS = {
  [PRESS_SETUP_LAYOUT_STATUS.UNSET]: 'Press setup incomplete',
  [PRESS_SETUP_LAYOUT_STATUS.DEFAULT]: 'Matches suggested default layout',
  [PRESS_SETUP_LAYOUT_STATUS.CUSTOM]: 'Differs from suggested layout, or has press setup notes — review before printing'
};

const locationLayoutSnapshot = (setup) => ({
  flashEnabled: setup?.flashEnabled === true,
  rollerEnabled: setup?.rollerEnabled === true,
  screens: (setup?.screens || [])
    .map((screen, index) => ({
      color: normalizeColorKey(screen.color),
      head: screen.head != null && screen.head !== '' ? Number(screen.head) : null,
      meshCount: resolveMeshCount(screen),
      printOrder: screen.printOrder ?? index
    }))
    .sort((a, b) => a.printOrder - b.printOrder)
});

const locationLayoutsMatch = (a, b) =>
  JSON.stringify(locationLayoutSnapshot(a)) === JSON.stringify(locationLayoutSnapshot(b));

export const locationHasHeadAssignments = (setup) => {
  if (!setup) return false;
  if (setup.flashEnabled === true || setup.rollerEnabled === true) return true;
  return (setup.screens || []).some(
    (screen) => screen.head != null && screen.head !== ''
  );
};

/** Every ink color on this location has a press head assigned. */
export const locationIsFullyAssigned = (setup) => {
  const screens = setup?.screens || [];
  if (screens.length === 0) return false;
  return screens.every((screen) => screen.head != null && screen.head !== '');
};

export const getSuggestedLocationLayout = (locationSetup, inkColors, inkCatalog = []) => {
  const colors = Array.isArray(inkColors)
    ? inkColors.filter((color) => String(color || '').trim())
    : (locationSetup?.screens || [])
        .map((screen) => screen.color)
        .filter((color) => String(color || '').trim());

  return applyDefaultHeadLayout(
    {
      flashEnabled: false,
      rollerEnabled: false,
      screens: colors.map((color, index) => ({
        color,
        meshCount: defaultMeshForColor(color),
        head: null,
        printOrder: index
      })),
      notes: locationSetup?.notes || ''
    },
    inkCatalog
  );
};

/**
 * Compares saved press setup against the layout Apply Default Layout would
 * produce for the current ink colors. Does not track whether that button was
 * clicked — only whether the result matches our suggestion.
 *
 * Grey  = incomplete (not every color has a head on every active location)
 * Green = complete, matches suggested default, and no setup notes
 * Orange = complete but differs from suggested default, and/or has press setup notes
 */
export const getPressSetupLayoutStatus = (pressSetup, lineData, inkCatalog = []) => {
  const activeLocations = getActiveLocations(lineData);
  if (activeLocations.length === 0) {
    return PRESS_SETUP_LAYOUT_STATUS.UNSET;
  }

  const synced = syncPressSetupWithLine(pressSetup, lineData);
  if (!synced) {
    return PRESS_SETUP_LAYOUT_STATUS.UNSET;
  }

  const allFullyAssigned = activeLocations.every((loc) =>
    locationIsFullyAssigned(synced[loc])
  );
  if (!allFullyAssigned) {
    return PRESS_SETUP_LAYOUT_STATUS.UNSET;
  }

  // Notes are a review flag — surface as orange even when heads match default.
  if (hasPressSetupNotes(pressSetup)) {
    return PRESS_SETUP_LAYOUT_STATUS.CUSTOM;
  }

  const allMatchSuggested = activeLocations.every((loc) =>
    locationLayoutsMatch(
      synced[loc],
      getSuggestedLocationLayout(
        pressSetup?.[loc],
        lineData?.[`${loc}InkColors`],
        inkCatalog
      )
    )
  );

  return allMatchSuggested
    ? PRESS_SETUP_LAYOUT_STATUS.DEFAULT
    : PRESS_SETUP_LAYOUT_STATUS.CUSTOM;
};

export const hasActiveInkColors = (inkColors) =>
  Array.isArray(inkColors) && inkColors.some((color) => String(color || '').trim());

export const getActiveLocations = (lineData) =>
  PRESS_LOCATIONS.filter((loc) => hasActiveInkColors(lineData?.[`${loc}InkColors`]));

export const getHeadOccupants = (locationSetup) => {
  const occupants = {};
  for (const screen of locationSetup.screens || []) {
    if (screen.head != null && screen.head >= 1 && screen.head <= 8) {
      occupants[screen.head] = {
        type: 'ink',
        label: screen.color,
        color: screen.color
      };
    }
  }
  if (locationSetup.flashEnabled === true) {
    occupants[FLASH_HEAD] = { type: 'flash', label: 'Flash' };
  }
  if (locationSetup.rollerEnabled === true) {
    occupants[ROLLER_HEAD] = { type: 'roller', label: 'Roller' };
  }
  return occupants;
};

export const getContrastTextColor = (hex) => {
  const normalized = normalizeInkHex(hex);
  if (!normalized) return 'var(--al-bg)';
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? 'var(--al-bg)' : '#FFFFFF';
};
